import * as React from "react";
import { FileText, Loader2 } from "lucide-react";

import { useLazyGetSupplierFamiliesQuery } from "@/api/suppliers";
import { Badge } from "@/components/ui/badge";
import type { ProcurementStatus } from "@/types/supplier";

import {
  ContractFamilyCategory,
  ContractFamilyExpiry,
  ContractValue,
  GbpEquivalent,
} from "./contract-family-enrichment";

const familyStatusVariant = (status: ProcurementStatus | null) => {
  if (status === "active") return "traffic-green" as const;
  if (status === "expiring") return "traffic-amber" as const;
  if (status === "expired" || status === "archived") return "traffic-red" as const;
  return "secondary" as const;
};

interface Props {
  supplierId: string;
  expanded: boolean;
  familyStatus?: ProcurementStatus[];
  familyCategory?: string[];
}

export const SupplierFamiliesExpansion = ({ supplierId, expanded, familyStatus, familyCategory }: Props) => {
  const [loadFamilies, { data, isFetching, isError }] = useLazyGetSupplierFamiliesQuery();

  React.useEffect(() => {
    if (expanded) {
      loadFamilies({
        supplierId,
        params: { family_status: familyStatus, family_category: familyCategory, limit: 50 },
      });
    }
  }, [expanded, familyCategory, familyStatus, loadFamilies, supplierId]);

  if (!expanded) return null;

  return (
    <div className="border-t border-slate-100 bg-slate-50/70 px-5 py-4">
      {isFetching && !data ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading Contract Families…
        </div>
      ) : null}
      {isError ? <p className="text-sm text-red-700">Contract Families could not be loaded.</p> : null}
      {data?.items.length === 0 ? <p className="text-sm text-slate-500">No accessible Contract Families.</p> : null}
      {data?.items.length ? (
        <ul className="space-y-2" aria-label="Accessible Contract Families">
          {data.items.map(family => (
            <li
              key={family.id}
              className="grid items-center gap-3 rounded-md bg-white px-3 py-2 md:grid-cols-[minmax(0,1fr)_minmax(0,14rem)_9rem_10rem]"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="rounded-md bg-violet-50 p-1.5 text-violet-700">
                  <FileText className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2">
                    <p className="truncate text-sm font-medium text-slate-900">{family.name}</p>
                    <Badge
                      aria-label={`${family.name} status`}
                      variant={familyStatusVariant(family.procurement_status)}
                      className="capitalize"
                    >
                      {family.procurement_status ?? "Not set"}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500">
                    {family.accessible_document_count} accessible{" "}
                    {family.accessible_document_count === 1 ? "document" : "documents"}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Pathfinder Family ID: {family.id}
                    {family.external_id ? ` · External ID: ${family.external_id}` : ""}
                  </p>
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Category</p>
                <ContractFamilyCategory category={family.category} className="mt-0.5" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Expiry</p>
                <ContractFamilyExpiry
                  expiryDate={family.expiry_date}
                  status={family.procurement_status}
                  className="mt-0.5"
                />
              </div>
              <div className="md:text-right">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Total value</p>
                <p
                  aria-label={`${family.name} Total Contract Value`}
                  className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900"
                >
                  <ContractValue value={family.estimated_total_contract_value} currencyCode={family.currency_code} />
                  <GbpEquivalent
                    value={family.estimated_total_contract_value_gbp}
                    currencyCode={family.currency_code}
                  />
                </p>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      {data && data.total > data.items.length ? (
        <p className="mt-3 text-xs text-slate-500">
          Showing the first {data.items.length} of {data.total} accessible families.
        </p>
      ) : null}
    </div>
  );
};
